/**
 * Moves the images a widget embeds (data: URIs) or loads from other sites into
 * Assets, and points the code at the new URLs. One image per request keeps
 * each request well under the server's size limit.
 */
import type { ImageRef } from 'shared/lib/widgetCode/normalizeWidgetCode';
import { decodeEntities } from 'shared/lib/widgetCode/inlineHandlers';

export type ImportImage = (source: string, folder: string) => Promise<{ url: string }>;

export type MoveImagesResult = {
  html: string;
  css: string;
  moved: Array<{ from: string; to: string }>;
  failed: Array<{ src: string; message: string }>;
};

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Points the code at the moved images. Only whole URLs are replaced (in an attribute,
 * url(), or a srcset entry), longest first, so ".../photo?w=800" never rewrites the
 * start of ".../photo?w=800&amp;h=600".
 */
export const applyMovedImages = (code: { html: string; css: string }, moved: MoveImagesResult['moved']): { html: string; css: string } => {
  let { html, css } = code;
  moved.slice().sort((a, b) => b.from.length - a.from.length).forEach(({ from, to }) => {
    const token = new RegExp(`(^|["'(\\s,=])${escapeRegExp(from)}(?=$|["')\\s,>])`, 'g');
    const swap = (text: string) => text.replace(token, (_m, lead: string) => `${lead}${to}`);
    html = swap(html);
    css = swap(css);
  });
  return { html, css };
};

export const MOVABLE_KINDS: ImageRef['kind'][] = ['base64', 'outside'];

export const moveImagesToAssets = async (
  code: { html: string; css: string },
  images: ImageRef[],
  folder: string,
  importImage: ImportImage
): Promise<MoveImagesResult> => {
  const moved: MoveImagesResult['moved'] = [];
  const failed: MoveImagesResult['failed'] = [];
  for (const image of images.filter(i => MOVABLE_KINDS.includes(i.kind))) {
    try {
      // Attribute values can carry HTML entities (e.g. &amp; in a query string), and
      // protocol-relative URLs (//cdn…) need a scheme before the server can fetch them.
      let source = image.kind === 'outside' ? decodeEntities(image.src) : image.src;
      if (source.startsWith('//')) source = `https:${source}`;
      const { url } = await importImage(source, folder); // eslint-disable-line no-await-in-loop
      moved.push({ from: image.src, to: url });
    } catch (error) {
      failed.push({ src: image.src, message: (error as Error).message || 'Upload failed' });
    }
  }
  return { ...applyMovedImages(code, moved), moved, failed };
};
